import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { format } from "date-fns";
import { PropsWithChildren } from "react";
import { useFragment } from "react-relay";

import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";
import { convertDateStringToObj } from "@whoosh/utilities/src/dates";

import { useIsPackageOrderDue } from "../../hooks/useIsPackageOrderDue";
import { PackageOrderSummary_order$key } from "./__generated__/PackageOrderSummary_order.graphql";

type Props = {
  orderRef: PackageOrderSummary_order$key | null | undefined;
};
export const PackageOrderSummary = ({ orderRef }: Props) => {
  const { currencySymbol } = useGetClubCurrency();
  const order = useFragment(PackageOrderSummaryFragmentString, orderRef);

  const isOrderDue = useIsPackageOrderDue(order);

  const packageOrders =
    order?.memberPackages?.edges?.flatMap((edge) =>
      edge?.node?.orders?.edges?.flatMap((orderEdge) =>
        orderEdge?.node ? [orderEdge.node] : []
      )
    ) ?? [];

  const nextPaymentDate = packageOrders.reverse()[0]?.packageDueDate ?? null;
  const formattedNextPaymentDate = nextPaymentDate
    ? format(convertDateStringToObj(nextPaymentDate), "MMM do, yyyy")
    : null;

  const shouldShowNextPaymentDate =
    !!formattedNextPaymentDate && (packageOrders.length > 1 || !isOrderDue);

  const lineItems =
    order?.lineItems?.edges?.flatMap((edge) =>
      edge?.node ? [edge.node] : []
    ) ?? [];

  const amountDueToday = isOrderDue ? order?.total || 0 : 0;

  return order ? (
    <VStack gap={6}>
      {isOrderDue ? (
        <VStack gap={4}>
          {lineItems.map((item) => (
            <VStack key={item.id} gap={4}>
              <Row key={item.id}>
                <Text>{item.name}</Text>
                <Text>
                  {currencySymbol}
                  {ConvertCentsToDollars(item.price)}
                </Text>
              </Row>
              {item.appliedDiscounts?.map((discount, i) => (
                <VStack key={discount?.name || `discount-${i}`}>
                  <Row>
                    <Text>{discount?.name}</Text>
                    <Text>
                      -{currencySymbol}
                      {discount?.amountCents
                        ? ConvertCentsToDollars(discount.amountCents)
                        : "0.00"}
                    </Text>
                  </Row>
                  {item.price > 0 && discount?.amountCents ? (
                    <Row>
                      <Text
                        color="text-secondary"
                        variant="mainTiny"
                      >{`${Math.round(
                        (discount.amountCents / item.price) * 100
                      )}% ${t`off`} ${item.name}`}</Text>
                    </Row>
                  ) : null}
                </VStack>
              ))}
            </VStack>
          ))}
          <Row>
            <Text>{t`Taxes`}</Text>
            {order.totalTaxes ? (
              <Text>
                {currencySymbol}
                {ConvertCentsToDollars(order.totalTaxes)}
              </Text>
            ) : null}
          </Row>
          <Row>
            <Text>{t`Total`}</Text>
            {order.total ? (
              <Text>
                {currencySymbol}
                {ConvertCentsToDollars(order.total)}
              </Text>
            ) : null}
          </Row>
        </VStack>
      ) : null}
      <Divider />
      <VStack gap={2}>
        <Row>
          <Text variant="mainHeading">{t`Pay today`}</Text>
          {order.total ? (
            <Text variant="mainHeading">
              {currencySymbol}
              {ConvertCentsToDollars(amountDueToday)}
            </Text>
          ) : null}
        </Row>
        {shouldShowNextPaymentDate ? (
          <Row>
            <Text color="text-secondary">{t`Next payment`}</Text>
            <Text color="text-secondary">{formattedNextPaymentDate}</Text>
          </Row>
        ) : null}
      </VStack>
    </VStack>
  ) : null;
};

const PackageOrderSummaryFragmentString = graphql`
  fragment PackageOrderSummary_order on Order {
    id
    total
    totalTaxes
    ...useIsPackageOrderDue_order
    memberPackages {
      edges {
        node {
          id
          orders(status: OPEN) {
            edges {
              node {
                packageDueDate
              }
            }
          }
        }
      }
    }
    lineItems {
      edges {
        node {
          id
          name
          price
          ... on BasicLineItem {
            appliedDiscounts {
              name
              amountCents
            }
          }
        }
      }
    }
  }
`;

const Divider = () => (
  <Box height={1} backgroundColor="surface-dividers" width="100%" />
);

const Row = ({ children }: PropsWithChildren) => (
  <HStack justifyContent="space-between" alignItems="center">
    {children}
  </HStack>
);
